export type VerificationViewerMode = "self" | "public" | "private";

export interface VerificationState {
  isVerified: boolean;
  label?: string;
}

/**
 * Which visual variant the UAE PASS mark takes: `"verified"` (full strength) or
 * `"unverified-cta"` (dimmed verify call-to-action). Kept dependency-free so the
 * gating rules can be unit-tested without a DOM or the tokens package.
 */
export type UaePassMarkVariant = "verified" | "unverified-cta";

/**
 * The mark is monochrome: it always takes the name's own text color, so it
 * reads as part of the name on every CV template and in both appearances. The
 * unverified call-to-action is that same color dimmed - never a second hue. A
 * fixed light gray used to stand in for "dim", which read as full white on a
 * dark CV.
 */
export const UAE_PASS_MARK_UNVERIFIED_OPACITY = 0.35;

export function resolveUaePassMarkOpacity(variant: UaePassMarkVariant): number {
  return variant === "verified" ? 1 : UAE_PASS_MARK_UNVERIFIED_OPACITY;
}

/**
 * Pure gating for the UAE PASS mark, given the authoritative verification value.
 *
 * - Verified -> `"verified"`, full strength.
 * - Unverified -> `"unverified-cta"`, dimmed, on every reader-facing view as
 *   well as the owner's own. Product decision (5 Aug 2026): readers should see
 *   the verification affordance either way, so a public CV does surface an
 *   unverified state. Whether the mark is *tappable* is a separate concern -
 *   only the owner's view passes an `onVerifyPress`.
 * - `null` only when the caller supplies no authoritative value at all, which is
 *   how a surface opts out of the mark entirely.
 *
 * `viewerMode` deliberately plays no part here any more: it used to hide the
 * unverified mark from public/private viewers.
 */
export function resolveUaePassMark(
  verification: VerificationState | null | undefined,
): UaePassMarkVariant | null {
  if (!verification) return null;
  return verification.isVerified ? "verified" : "unverified-cta";
}
